import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import docs from '~/assets/icons/docs_2.svg';
import question from '~/assets/icons/question.svg';
import { Button, ButtonColors, ButtonHeight } from '~/components/ui/Button';
import { Text } from '~/components/ui/Text';
import { rage, useObservable } from '~/rage';
import { ReportMessageType, ReportState, Role } from '~/shared';
import {
	setActiveReportId,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import { ReportChatMessage } from '../ReportChatMessage';
import {
	StyledAdminButtonsContainer,
	StyledAdminEmptyContainer,
	StyledContainer,
	StyledDateTimeTag,
	StyledEmptyContainer,
	StyledHeadContainer,
	StyledHeadLeftContainer,
	StyledHeadRightContainer,
	StyledHelpTag,
	StyledInput,
	StyledInputContainer,
	StyledLoadingContainer,
	StyledMessagesContainer,
	StyledPendingTag,
	StyledPlayerButtonsContainer,
	StyledStatusTag,
} from './ReportChat.styled';
import { StaffNicknames } from './StaffNicknames';

const ReportChat = () => {
	const dispatch = useLayoutDispatch();
	const reports = useObservable(rage.reports.getReports());
	const activeReportId = useLayoutSelector(
		(state) => state.mainMenu.activeReportId,
	);
	const { messages, loading } = useObservable(
		rage.reports.getActiveReportMessages(),
	);
	const activeReport = useMemo(
		() => reports.find((report) => report.id === activeReportId),
		[reports, activeReportId],
	);

	const currentRole = useObservable(rage.accessControl.getCurrentRole());
	const isStaffMember = useMemo(
		() => rage.accessControl.hasMinimumRole(Role.MODERATOR),
		[currentRole],
	);

	const [inputValue, setInputValue] = useState('');
	const buttonDisabled = useMemo(() => !inputValue.trim(), [inputValue]);
	const messagesContainerRef = useRef<HTMLDivElement>(null);
	const prevActiveReportIdRef = useRef<string | null>(null);

	const staffNames: string[] = isStaffMember
		? activeReport?.viewingStaff ?? []
		: activeReport?.participants ?? [];

	useEffect(() => {
		rage.reports.setActiveReportId(activeReportId);
	}, [activeReportId]);

	useEffect(() => {
		if (isStaffMember) {
			const prevReportId = prevActiveReportIdRef.current;

			if (prevReportId && prevReportId !== activeReportId) {
				rage.reports
					.leaveReport(prevReportId)
					.catch((err) => rage.notifications.showException(err));
			}

			if (activeReportId) {
				rage.reports
					.joinReport(activeReportId)
					.catch((err) => rage.notifications.showException(err));
			}

			prevActiveReportIdRef.current = activeReportId;
		}

		return () => {
			if (isStaffMember && activeReportId) {
				rage.reports
					.leaveReport(activeReportId)
					.catch((err) => rage.notifications.showException(err));
			}
		};
	}, [activeReportId, isStaffMember]);

	useEffect(() => {
		if (messagesContainerRef.current) {
			messagesContainerRef.current.scrollTop =
				messagesContainerRef.current.scrollHeight;
		}
	}, [activeReportId]);

	useEffect(() => {
		if (messagesContainerRef.current && messages.length) {
			messagesContainerRef.current.scrollTop =
				messagesContainerRef.current.scrollHeight;
		}
	}, [messages.length]);

	const handleSendMessage = async () => {
		if (buttonDisabled) return;

		if (!activeReportId) {
			// Clear internal service state immediately to ensure new report creation
			rage.reports.setActiveReportId(null);

			// User creates a new report - response includes report and messages
			const { report, messages } = await rage.reports.createReport(
				inputValue.trim(),
			);

			// Preload messages into cache so setActiveReportId uses them instead of fetching
			rage.reports.preloadMessages(report.id, messages);

			// Set active report - will use cached messages instead of fetching
			dispatch(setActiveReportId(report.id));

			setInputValue('');
			return;
		}

		rage.reports
			.sendMessage(activeReportId, inputValue.trim())
			.catch((err) => rage.notifications.showException(err));
		setInputValue('');
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter' && !buttonDisabled) {
			handleSendMessage().catch((err) => rage.notifications.showException(err));
		}
	};

	const adminButtonsAlign = useMemo(() => {
		const lastMessage = messages[messages.length - 1];
		const messageType = isStaffMember
			? lastMessage?.isStaffMessage
				? ReportMessageType.OUTGOING
				: ReportMessageType.INCOMING
			: lastMessage?.isStaffMessage
			? ReportMessageType.INCOMING
			: ReportMessageType.OUTGOING;

		const styles = {
			justifyContent:
				messageType === ReportMessageType.OUTGOING ? 'flex-end' : 'flex-start',
			marginLeft: '',
		};
		if (messageType === ReportMessageType.OUTGOING) {
			styles.marginLeft = 'auto';
		}
		return styles;
	}, [isStaffMember, messages]);

	const handleSetHelpNeeded = () => {
		if (activeReportId) {
			rage.reports
				.setHelpNeeded(activeReportId, !activeReport?.isHelpNeeded)
				.catch((err) => rage.notifications.showException(err));
		}
	};

	const handleToggleTicket = () => {
		if (activeReportId) {
			const isClosedOrCompleted =
				activeReport?.state === ReportState.CLOSED ||
				activeReport?.state === ReportState.COMPLETED;
			if (isClosedOrCompleted) {
				rage.reports
					.setReportActive(activeReportId)
					.catch((err) => rage.notifications.showException(err));
			} else {
				rage.reports
					.setReportCompleted(activeReportId)
					.catch((err) => rage.notifications.showException(err));
			}
		}
	};

	const handlePlayerCloseTicket = () => {
		if (activeReportId) {
			rage.reports
				.setReportClosed(activeReportId)
				.catch((err: unknown) => rage.notifications.showException(err));
		}
	};

	const handleSpectatePlayer = () => {
		if (activeReport?.characterId) {
			rage.reports
				.spectate(activeReport.characterId)
				.catch((err) => rage.notifications.showException(err));
		}
	};

	const handleTeleportToPlayer = () => {
		if (activeReport?.characterId) {
			rage.reports
				.tpToPlayer(activeReport.characterId)
				.catch((err) => rage.notifications.showException(err));
		}
	};

	const handleTeleportPlayerToMe = () => {
		if (activeReport?.characterId) {
			rage.reports
				.tpPlayerToMe(activeReport.characterId)
				.catch((err) => rage.notifications.showException(err));
		}
	};

	return (
		<StyledContainer>
			{activeReportId && (
				<StyledHeadContainer>
					<StyledHeadLeftContainer>
						<img src={question} />
						<Text>Обращение #{activeReport?.id || 'N/A'}</Text>
					</StyledHeadLeftContainer>
					<StyledHeadRightContainer>
						{isStaffMember &&
							activeReport?.isHelpNeeded &&
							activeReport?.state === ReportState.ACTIVE && (
								<StyledHelpTag>
									<Text>{'Нужна помощь'}</Text>
								</StyledHelpTag>
							)}
						{isStaffMember &&
							activeReport?.state === ReportState.ACTIVE &&
							!activeReport?.lastMessageIsFromStaff && (
								<StyledPendingTag>
									<Text>{'Ожидание ответа'}</Text>
								</StyledPendingTag>
							)}
						{isStaffMember && (
							<StyledStatusTag className={activeReport?.state}>
								<Text>
									{activeReport?.state === ReportState.CLOSED && 'Закрыто'}
									{activeReport?.state === ReportState.COMPLETED && 'Завершено'}
									{activeReport?.state === ReportState.ACTIVE && 'Активное'}
								</Text>
							</StyledStatusTag>
						)}
						<StaffNicknames staffNames={staffNames} />
						<StyledDateTimeTag>
							<Text className="date">
								{new Date(activeReport?.createdAt || '').toLocaleDateString(
									'ru-RU',
									{
										day: '2-digit',
										month: '2-digit',
										year: '2-digit',
										timeZone: 'Europe/Moscow',
									},
								)}
							</Text>
							<Text>
								{new Date(activeReport?.createdAt || '').toLocaleTimeString(
									'ru-RU',
									{ hour12: false, timeZone: 'Europe/Moscow' },
								)}
							</Text>
						</StyledDateTimeTag>
					</StyledHeadRightContainer>
				</StyledHeadContainer>
			)}
			{!activeReportId && (
				<Fragment>
					{isStaffMember ? (
						<StyledAdminEmptyContainer>
							<img src={docs} />
							<Text>Выберите один из доступных обращений в панеле слева</Text>
						</StyledAdminEmptyContainer>
					) : (
						<StyledEmptyContainer>
							<Text>
								Для начала диалога, опишите свою проблему сообщением ниже
							</Text>
						</StyledEmptyContainer>
					)}
				</Fragment>
			)}
			{activeReportId && loading && (
				<StyledLoadingContainer>
					<Text>Подождите, идёт загрузка…</Text>
				</StyledLoadingContainer>
			)}
			{activeReportId && !loading && (
				<StyledMessagesContainer ref={messagesContainerRef} tabIndex={-1}>
					{messages.map((message, index) => {
						const previousMessage = index > 0 ? messages[index - 1] : undefined;
						const isContinuation = !!(
							previousMessage &&
							previousMessage.characterId === message.characterId
						);
						const isFirst = index === 0;

						return (
							<ReportChatMessage
								key={message.id}
								characterId={message.characterId}
								playerName={message.playerName}
								accountId={message.accountId}
								createdAt={message.createdAt}
								text={message.message}
								online={activeReport?.playerOnline ?? false}
								isStaff={isStaffMember}
								isStaffMessage={message.isStaffMessage}
								isContinuation={isContinuation}
								isFirst={isFirst}
							/>
						);
					})}
					{isStaffMember && (
						<StyledAdminButtonsContainer style={adminButtonsAlign}>
							{activeReport?.state === ReportState.ACTIVE && (
								<Button
									color={ButtonColors.Default}
									height={ButtonHeight.Height40}
									onClick={handleSetHelpNeeded}
								>
									<Text>
										{activeReport?.isHelpNeeded
											? 'Не нужна помощь'
											: 'Нужна помощь'}
									</Text>
								</Button>
							)}
							<Button
								color={ButtonColors.Default}
								height={ButtonHeight.Height40}
								onClick={handleToggleTicket}
							>
								<Text>
									{activeReport?.state === ReportState.CLOSED ||
									activeReport?.state === ReportState.COMPLETED
										? 'Открыть обращение'
										: 'Закрыть обращение'}
								</Text>
							</Button>
							{activeReport?.playerOnline && (
								<>
									<Button
										color={ButtonColors.Default}
										height={ButtonHeight.Height40}
										onClick={handleSpectatePlayer}
									>
										<Text>Следить за игроком</Text>
									</Button>
									<Button
										color={ButtonColors.Default}
										height={ButtonHeight.Height40}
										onClick={handleTeleportToPlayer}
									>
										<Text>Телепорироваться</Text>
									</Button>
									<Button
										color={ButtonColors.Default}
										height={ButtonHeight.Height40}
										onClick={handleTeleportPlayerToMe}
									>
										<Text>Телепортировать к себе</Text>
									</Button>
								</>
							)}
						</StyledAdminButtonsContainer>
					)}
					{!isStaffMember && activeReport?.state === ReportState.ACTIVE && (
						<StyledPlayerButtonsContainer>
							<Button
								color={ButtonColors.Default}
								height={ButtonHeight.Height40}
								onClick={handlePlayerCloseTicket}
							>
								<Text>Закрыть обращение</Text>
							</Button>
						</StyledPlayerButtonsContainer>
					)}
				</StyledMessagesContainer>
			)}
			<StyledInputContainer
				$hidden={
					(!activeReportId && isStaffMember) ||
					(activeReport && activeReport.state !== ReportState.ACTIVE)
				}
			>
				<StyledInput
					placeholder="Введите сообщение"
					value={inputValue}
					onChange={(e) => setInputValue(e.target.value)}
					onKeyDown={handleKeyDown}
				/>
				<Button
					color={ButtonColors.Select}
					height={ButtonHeight.Height40}
					style={{ borderRadius: 5, fontWeight: 500 }}
					disabled={buttonDisabled}
					onClick={handleSendMessage}
				>
					<Text>Отправить сообщение</Text>
				</Button>
			</StyledInputContainer>
		</StyledContainer>
	);
};

export { ReportChat };
